import { useRef, type PointerEvent as ReactPointerEvent } from "react";
import { grantBlock, moveBlocks, revokeBlock, snap, updateBlock, type Point } from "@worktools/shared";
import { useUi } from "../state/ui";
import { useBoard } from "../sync/BoardContext";
import { bringToFront } from "../board/actions";
import { useMyGrants, type BlockFrame } from "../sync/hooks";
import { ChecklistBlock } from "./blocks/ChecklistBlock";
import { TimerBlock } from "./blocks/TimerBlock";
import { NoteBlock } from "./blocks/NoteBlock";
import { TextBlock } from "./blocks/TextBlock";
import { CardBlock } from "./blocks/CardBlock";
import { TableBlock } from "./blocks/TableBlock";
import { DiagramBlock } from "./blocks/DiagramBlock";

const KIND_LABEL: Record<string, string> = {
  checklist: "Checkliste",
  timer: "Timer",
  note: "Notiz",
  text: "Text",
  card: "Karte",
  table: "Tabelle",
  diagram: "Diagramm",
};

const MIN_SIZE = { w: 180, h: 120 };

type Corner = "nw" | "ne" | "sw" | "se";

export function BlockShell({
  frame,
  toCanvas,
}: {
  frame: BlockFrame;
  toCanvas: (event: { clientX: number; clientY: number }) => Point;
}) {
  const { doc, me } = useBoard();
  const grants = useMyGrants();
  const selection = useUi((state) => state.selection);
  const setSelection = useUi((state) => state.setSelection);
  const toggleSelection = useUi((state) => state.toggleSelection);
  const openContextMenu = useUi((state) => state.openContextMenu);
  const closeContextMenu = useUi((state) => state.closeContextMenu);
  const snapToGrid = useUi((state) => state.snapToGrid);
  const tool = useUi((state) => state.tool);
  const last = useRef<Point | null>(null);
  const selected = selection.includes(frame.id);
  const grantMode = tool === "grant";
  const granted = Boolean(grants[frame.id]);

  const toggleGrant = () => {
    if (granted) revokeBlock(doc, frame.id, me.id);
    else grantBlock(doc, frame.id, { id: me.id, name: me.name });
  };

  const startMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (frame.locked || tool !== "select" || event.button !== 0) return;
    event.stopPropagation();
    closeContextMenu();

    const ids = selected ? selection : [frame.id];
    if (!selected) setSelection([frame.id]);
    bringToFront(doc, ids);

    last.current = toCanvas(event);
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);

    const move = (moveEvent: PointerEvent) => {
      if (!last.current) return;
      const point = toCanvas(moveEvent);
      const delta = { x: point.x - last.current.x, y: point.y - last.current.y };
      last.current = point;
      moveBlocks(doc, ids, delta);
    };

    const stop = () => {
      last.current = null;
      if (snapToGrid) {
        doc.transact(() => {
          for (const id of ids) {
            const map = doc.getMap<any>("blocks").get(id);
            if (!map) continue;
            map.set("x", snap(map.get("x"), 24));
            map.set("y", snap(map.get("y"), 24));
          }
        });
      }
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", stop);
      target.removeEventListener("pointercancel", stop);
    };

    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", stop);
    target.addEventListener("pointercancel", stop);
  };

  const startResize = (event: ReactPointerEvent<HTMLElement>, corner: Corner) => {
    event.stopPropagation();
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);
    const origin = toCanvas(event);
    const start = { x: frame.x, y: frame.y, w: frame.w, h: frame.h };

    const move = (moveEvent: PointerEvent) => {
      const point = toCanvas(moveEvent);
      const dx = point.x - origin.x;
      const dy = point.y - origin.y;
      const next = { ...start };

      if (corner === "se") {
        next.w = Math.max(MIN_SIZE.w, start.w + dx);
        next.h = Math.max(MIN_SIZE.h, start.h + dy);
      } else if (corner === "sw") {
        next.w = Math.max(MIN_SIZE.w, start.w - dx);
        next.h = Math.max(MIN_SIZE.h, start.h + dy);
        next.x = start.x + (start.w - next.w);
      } else if (corner === "ne") {
        next.w = Math.max(MIN_SIZE.w, start.w + dx);
        next.h = Math.max(MIN_SIZE.h, start.h - dy);
        next.y = start.y + (start.h - next.h);
      } else {
        next.w = Math.max(MIN_SIZE.w, start.w - dx);
        next.h = Math.max(MIN_SIZE.h, start.h - dy);
        next.x = start.x + (start.w - next.w);
        next.y = start.y + (start.h - next.h);
      }

      updateBlock(doc, frame.id, {
        x: Math.round(next.x),
        y: Math.round(next.y),
        w: Math.round(next.w),
        h: Math.round(next.h),
      });
    };

    const stop = () => {
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", stop);
    };

    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", stop);
  };

  return (
    <div
      className={[
        "block",
        selected && !grantMode ? "block--selected" : "",
        frame.locked ? "block--locked" : "",
        grantMode ? (granted ? "block--granted" : "block--dimmed") : "",
        !grantMode && granted ? "block--shared" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ left: frame.x, top: frame.y, width: frame.w, height: frame.h, zIndex: frame.z }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        if (grantMode) {
          event.stopPropagation();
          toggleGrant();
          return;
        }
        if (event.shiftKey) {
          event.stopPropagation();
          toggleSelection(frame.id);
          return;
        }
        if (!selected) setSelection([frame.id]);
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!selected) setSelection([frame.id]);
        openContextMenu({
          screen: { x: event.clientX, y: event.clientY },
          target: { kind: "block", blockId: frame.id, canvasPoint: toCanvas(event) },
        });
      }}
    >
      {frame.accent && <div className="block__accent" style={{ background: frame.accent }} />}
      {grantMode && <div className="block__grantveil" />}
      {granted && (
        <div className="block__shared" title="Für deinen Claude freigegeben">
          <img src="/claude-logo.svg" alt="" />
          Claude
        </div>
      )}
      <header className="block__head" onPointerDown={startMove}>
        <span className="block__kind">{KIND_LABEL[frame.type] ?? frame.type}</span>
        <span style={{ flex: 1 }} />
        {frame.locked && <span className="block__kind">gesperrt</span>}
      </header>
      <div className="block__body scroll">
        <BlockBody frame={frame} />
      </div>

      {selected && !frame.locked && !grantMode && (
        <>
          {(["nw", "ne", "sw", "se"] as Corner[]).map((corner) => (
            <div
              key={corner}
              className={`handle handle--${corner}`}
              onPointerDown={(event) => startResize(event, corner)}
            />
          ))}
        </>
      )}
    </div>
  );
}

function BlockBody({ frame }: { frame: BlockFrame }) {
  switch (frame.type) {
    case "checklist":
      return <ChecklistBlock blockId={frame.id} />;
    case "timer":
      return <TimerBlock blockId={frame.id} />;
    case "note":
      return <NoteBlock blockId={frame.id} />;
    case "text":
      return <TextBlock blockId={frame.id} />;
    case "card":
      return <CardBlock blockId={frame.id} />;
    case "table":
      return <TableBlock blockId={frame.id} />;
    case "diagram":
      return <DiagramBlock blockId={frame.id} />;
    default:
      return null;
  }
}
